import clsx from 'clsx';
import type { ImageProps } from 'next/image';
import type { CSSProperties } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import VideoPlayer from '@commons/components/VideoPlayer';
import { asAbsoluteURL } from '@commons/helpers/url';
import type { DrupalImage as DrupalImageType, DrupalVideo } from '@commons/types/drupal';
import type { Breakpoint } from '@commons/types/styles';

import styles from './Media.module.scss';

// Type definition for the ImageBackground component properties.

type ImageBackgroundProps = DrupalImageType & {
  fill?: boolean;
  sizes?: ImageProps['sizes'];
  priority?: boolean;
  className?: string;
};

/**
 * ImageBackground component.
 *
 * Used when the background type is `media` with an image.
 *
 * IMPORTANT:
 *
 * - The `fill` property should not be set by default, but can be passed if needed.
 *   This is because most sections are really tall on mobile and the image generated by Next.js is too small.
 *   Doing so will cause the image to be blurry.
 *
 * - Setting unoptimized to `true` because the image size gets bigger after the image is optimized in Vercel.
 *   WebP why you do this to me?
 */

const ImageBackground = (props: ImageBackgroundProps) => <DrupalImage sizes="100vw" {...props} />;

/**
 * VideoBackground component.
 *
 * Used when the background type is `media` with a video.
 */

const VideoBackground = ({ url, className }: { url: string; className?: string }) => (
  <VideoPlayer
    src={asAbsoluteURL(url)}
    loop
    muted
    playing
    autoPlay
    playsInline
    width="100%"
    height="100%"
    volume={0}
    controls={false}
    fullScreen
    roundCorners={false}
    displayLoading={false}
    className={className}
  />
);

// Type definition for the BackgroundFactory component properties.

type BackgroundFactoryProps = {
  media: DrupalImageType | DrupalVideo;
  priority?: boolean;
  className?: string;
};

/**
 * BackgroundFactory component.
 *
 * Renders the appropriate media background based on its bundle.
 */

const BackgroundFactory = ({ media, priority, className }: BackgroundFactoryProps) => {
  switch (media.bundle) {
    case 'image':
      return (
        <ImageBackground {...media} priority={priority} className={clsx(styles.image, className)} />
      );
    case 'video':
      return <VideoBackground url={media.url} className={className} />;
  }
};

/**
 * Type definition for the Background component properties.
 */

type BackgroundMediaProps = {
  media: DrupalImageType | DrupalVideo;
  mobileMedia?: DrupalImageType | DrupalVideo;
  breakpoint?: Breakpoint;
  overlayOpacity?: number;
  sizes?: ImageProps['sizes'];
  priority?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * MediaBackground component.
 *
 * Renders a background image or video, with an optional overlay.
 * Additionally, it can render a different media on mobile devices.
 *
 * Defined as a seperate component to ensure that the sections can be rendered as server components.
 */

const MediaBackground = (props: BackgroundMediaProps) => {
  const {
    media,
    breakpoint = 'md',
    mobileMedia,
    overlayOpacity = 0,
    priority,
    className,
    style,
  } = props;

  // Early return if no media is provided.
  if (!media?.url) return null;

  const hasMobileMedia = mobileMedia && mobileMedia.url;

  const classes = clsx(
    styles.background,
    hasMobileMedia && [styles.responsive, styles[breakpoint]],
    className
  );

  return (
    <div className={classes} style={style}>
      <BackgroundFactory media={media} priority={priority} className={styles.media} />
      {hasMobileMedia && (
        <BackgroundFactory media={mobileMedia} priority={priority} className={styles.mobileMedia} />
      )}
      <div className={styles.overlay} style={{ opacity: overlayOpacity }} />
    </div>
  );
};

export default MediaBackground;
